import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import clsx from 'clsx';
import { chain } from '@react-aria/utils';
import { PressEvents } from '@react-types/shared';
import { useControlledState } from '@react-stately/utils';
import textFieldStyles from '@danske/sapphire-css/components/textField/textField.module.css';

import { TextFieldBase } from './TextFieldBase';
import { TextFieldRef } from '..';
import {
  SapphireTextFieldProps,
  useSapphireTextField,
} from './useSapphireTextField';
import { SapphireStyleProps, useThemeCheck } from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

type TextAreaResizableProps =
  | { minRows?: number; maxRows?: number; rows?: never; resizable: 'auto' }
  | { minRows?: never; maxRows?: never; rows?: number; resizable?: boolean };

export type TextAreaProps = SapphireTextFieldProps<'textarea'> &
  PressEvents &
  SapphireStyleProps &
  GlobalDomAttributes &
  TextAreaResizableProps & {
    /**
     * @default 'lg'
     */
    size?: 'lg' | 'md';
    /**
     * Places the label either above (default) or on the side of the control.
     * @default 'above'
     */
    labelPlacement?: 'side' | 'above';
  };

export const TextArea = forwardRef(function TextArea(
  props: TextAreaProps,
  ref: ForwardedRef<TextFieldRef>
) {
  useThemeCheck();

  const { onChange, minRows, maxRows, rows, resizable, ...otherProps } = props;
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [inputValue, setInputValue] = useControlledState(
    props.value,
    props.defaultValue || '',
    () => {
      // do nothing
    }
  );
  const onHeightChange = useCallback(() => {
    if (inputRef.current && resizable === 'auto') {
      const input = inputRef.current;
      const { lineHeight, paddingTop, paddingBottom } = window.getComputedStyle(
        inputRef.current
      );
      const getTotalHeight = (rowCount: number) => {
        return (
          parseInt(lineHeight, 10) * rowCount +
          parseInt(paddingTop) +
          parseInt(paddingBottom)
        );
      };
      const minHeight = minRows ? getTotalHeight(minRows) : 1;
      const maxHeight = maxRows ? getTotalHeight(maxRows) : Infinity;
      input.style.height = 'auto';
      const clampedHeight = Math.max(
        Math.min(input.scrollHeight, maxHeight),
        minHeight
      );
      input.style.height = `${clampedHeight}px`;
    }
  }, [inputRef]);

  useLayoutEffect(() => {
    if (inputRef.current) {
      onHeightChange();
    }
  }, [onHeightChange, inputValue, inputRef]);

  const { labelProps, inputProps, errorMessageProps, descriptionProps } =
    useSapphireTextField(
      {
        ...props,
        onChange: chain(onChange, setInputValue),
        inputElementType: 'textarea',
      },
      inputRef
    );

  return (
    <TextFieldBase
      {...otherProps}
      ref={ref}
      inputRef={inputRef}
      labelProps={labelProps}
      inputProps={{
        ...inputProps,
        rows:
          resizable !== 'auto' && rows
            ? rows
            : minRows && minRows < 2
            ? 1
            : undefined,
      }}
      errorMessageProps={errorMessageProps}
      descriptionProps={descriptionProps}
      inputElementType={'textarea'}
      containerClassName={clsx(
        [textFieldStyles['sapphire-text-field--multiline']],
        {
          [textFieldStyles['sapphire-text-field--resizable']]:
            props.resizable === true,
        }
      )}
    />
  );
});
